import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { makeStyles } from '@material-ui/core/styles';
import { Button, Box, CircularProgress, Fade, Paper, Typography } from '@material-ui/core';

/**
 * @constant visiblePills
 * @description Every pill that currently wants to be on screen, in the order they asked.
 * They are all the same component in the same fixed spot at the bottom of the screen, and more than
 * one is mounted at a time on purpose: the page keeps one for whatever is announced globally, and
 * the step on it renders its own for its own wait. When both wanted to show, the two pills sat
 * exactly on top of each other - which is the "two loadings" that shows up now and then.
 * Only one is drawn. A pill that names its wait wins over the page's general one, since it knows
 * more; among equals the first to ask keeps it, so the indicator does not flicker between them.
 * @memberof module:Core
 */
const visiblePills = [];

/**
 * @constant pillSubscribers
 * @description The mounted pills, to be told when the queue changes.
 * @memberof module:Core
 */
const pillSubscribers = new Set();

/**
 * @function topPill
 * @description Which pill gets to be the one on screen.
 * @memberof module:Core
 * @returns {Object|null} The winning entry, or null when nothing wants to show.
 */
const topPill = () =>
  visiblePills.reduce((best, entry) => (!best || entry.priority > best.priority ? entry : best), null);

/**
 * @function announcePills
 * @description Tells every mounted pill that the queue changed, so the winner can redraw.
 * @memberof module:Core
 * @returns {void}
 */
const announcePills = () => {
  const winner = topPill();
  pillSubscribers.forEach((notify) => notify(winner));
};

const useTextualStyles = makeStyles((theme) => ({
  text: {
    padding: theme.spacing(4, 0),
    color: theme.palette.grey[600],
  },
}));

const Textual = ({ customSentence = '' }) => {
  const [t] = useTranslation();
  const classes = useTextualStyles();

  return (
    <Typography className={classes.text} variant="subtitle1" align="center">
      {customSentence !== '' ? customSentence : t('general.loading')}
    </Typography>
  );
};

const useStyles = makeStyles((theme) => ({
  wrapper: {
    zIndex: 1400,
    position: 'fixed',
    left: '50%',
    transform: 'translateX(-50%)',
    bottom: theme.spacing(3),
    maxWidth: `calc(100vw - ${theme.spacing(4)}px)`,
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.75, 3),
    borderRadius: theme.spacing(3),
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.18)',
  },
  abort: {
    marginLeft: theme.spacing(1),
  },
  /**
   * Title and detail stack, so a long detail wraps under the title instead of pushing the pill
   * across the screen.
   */
  lines: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  loading: {
    fontWeight: 600,
    letterSpacing: 0.2,
  },
  /**
   * The part that keeps changing - which image, how many are left. Deliberately quieter than the
   * title: the title says what the wait is for and stays put, this says how it is going.
   */
  detail: {
    color: theme.palette.text.secondary,
    lineHeight: 1.3,
  },
}));

/**
 * @function ActivityIndicator
 * @description The app's one loading indicator: a pill at the bottom of the screen saying what is
 * being waited on, with the running detail under it.
 * There used to be three of these at once - this pill, a snackbar in the corner and a spinner in
 * the top bar - all for the same wait, and none of them saying what was being loaded. This is the
 * one that stays; anything that needs to report a wait either renders it with a `title`, or
 * announces one through `Common.notice` and lets the page-level one show it.
 * Several of these are mounted at once by design - see {@link visiblePills} for why only one of
 * them is ever drawn.
 * @param {Object} props
 * @param {Function} [props.onAbort] - Offers the user a way out of the wait, when there is one.
 * @param {Boolean} [props.textual] - Renders as a line of text in the page flow instead of a pill.
 * @param {String} [props.customSentence] - The text, for the textual form.
 * @param {Boolean} [props.forceShow] - Shows the pill regardless of the global job counter.
 * @param {String} [props.title] - What is being loaded. Falls back to whatever was announced
 *  globally, and only then to a bare "loading".
 * @param {String} [props.detail] - The part that keeps changing.
 * **Called by:
 * @see {MainPage} - once, for anything announced globally.
 * @see {AOIChooser}, {PeriodChooser}, {ProcessingPage} - for their own waits.
 * @memberof module:Core
 * @returns {JSX.Element} The indicator.
 */
const ActivityIndicator = ({ onAbort, textual, customSentence = '', forceShow = false, title, detail }) => {
  const working = useSelector((state) => state.common.working);
  const notice = useSelector((state) => state.common.notice);

  const [t] = useTranslation();
  const classes = useStyles();

  const heading = title || notice?.title || t('general.loading');
  const subtitle = detail || (title ? '' : notice?.detail) || '';
  const shown = working || forceShow || Boolean(notice?.title);

  /**
   * A pill that was given something specific to say outranks the page's general one. `forceShow`
   * counts as specific: it belongs to a step that knows it is waiting.
   */
  const entryRef = useRef({ priority: 1 });
  const [winner, setWinner] = useState(() => topPill());

  useEffect(() => {
    const notify = (next) => setWinner(next);
    pillSubscribers.add(notify);
    return () => {
      pillSubscribers.delete(notify);
    };
  }, []);

  useEffect(() => {
    const entry = entryRef.current;
    if (textual || !shown) return undefined;
    entry.priority = title || forceShow ? 2 : 1;
    visiblePills.push(entry);
    announcePills();
    return () => {
      const at = visiblePills.indexOf(entry);
      if (at !== -1) visiblePills.splice(at, 1);
      announcePills();
    };
  }, [textual, shown, title, forceShow]);

  const isTheOneOnScreen = winner === entryRef.current;

  return textual ? (
    <Textual customSentence={customSentence} />
  ) : (
    <Fade in={shown && isTheOneOnScreen} timeout={400} unmountOnExit>
      <Box className={classes.wrapper}>
        <Paper elevation={0} className={classes.content}>
          <CircularProgress thickness={4.5} size={26} color="primary" />
          <div className={classes.lines}>
            <Typography className={classes.loading} variant="subtitle1">
              {heading}
            </Typography>
            {subtitle && (
              <Typography className={classes.detail} variant="caption">
                {subtitle}
              </Typography>
            )}
          </div>
          {onAbort && (
            <Button className={classes.abort} color="error" size="small" onClick={onAbort}>
              {t('general.cancel')}
            </Button>
          )}
        </Paper>
      </Box>
    </Fade>
  );
};

export default ActivityIndicator;
